var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _cache, _abortControllersBySectionId, _pendingRendersBySectionId, _pendingPromises, _SectionRenderer_instances, renderSection_fn, abortPendingMorph_fn, cachePageSections_fn;
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { fetchWithDeadline, fetchCartJson } from "@theme/utilities";
class SectionRenderer {
  constructor() {
    __privateAdd(this, _SectionRenderer_instances);
    __privateAdd(this, _cache, /* @__PURE__ */ new Map());
    __privateAdd(this, _abortControllersBySectionId, /* @__PURE__ */ new Map());
    __privateAdd(this, _pendingRendersBySectionId, /* @__PURE__ */ new Map());
    __privateAdd(this, _pendingPromises, /* @__PURE__ */ new Map());
    window.addEventListener("load", __privateMethod(this, _SectionRenderer_instances, cachePageSections_fn).bind(this));
  }
  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {boolean} [options.injectStylesheet=false] - When true, extracts
   * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = "full", injectStylesheet = false } = options != null ? options : {};
    const { url } = options != null ? options : {};
    __privateMethod(this, _SectionRenderer_instances, abortPendingMorph_fn).call(this, sectionId);
    const abortController = new AbortController();
    __privateGet(this, _abortControllersBySectionId).set(sectionId, abortController);
    const renderPromise = __privateMethod(this, _SectionRenderer_instances, renderSection_fn).call(this, sectionId, { cache, mode, injectStylesheet, url }, abortController);
    __privateGet(this, _pendingRendersBySectionId).set(sectionId, { abortController, promise: renderPromise });
    return renderPromise;
  }
  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - A signal that cancels the section render fetch
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const shouldSharePendingPromise = !signal;
    let pendingPromise = shouldSharePendingPromise ? __privateGet(this, _pendingPromises).get(sectionUrl) : null;
    if (pendingPromise) return pendingPromise;
    if (useCache) {
      const cachedHTML = __privateGet(this, _cache).get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", onAbort);
    }
    pendingPromise = fetchWithDeadline(sectionUrl, { cache: useCache ? "default" : "no-store" }, controller, 15e3, (response) => {
      if (!response.ok) throw new Error("Failed to fetch section ".concat(sectionId, ": ").concat(response.status));
      return response.text();
    }).finally(() => {
      if (signal) signal.removeEventListener("abort", onAbort);
    });
    if (shouldSharePendingPromise) {
      pendingPromise = pendingPromise.finally(() => {
        __privateGet(this, _pendingPromises).delete(sectionUrl);
      });
      __privateGet(this, _pendingPromises).set(sectionUrl, pendingPromise);
    }
    const sectionHTML = await pendingPromise;
    __privateGet(this, _cache).set(sectionUrl, sectionHTML);
    return sectionHTML;
  }
}
_cache = /* @__PURE__ */ new WeakMap();
_abortControllersBySectionId = /* @__PURE__ */ new WeakMap();
_pendingRendersBySectionId = /* @__PURE__ */ new WeakMap();
_pendingPromises = /* @__PURE__ */ new WeakMap();
_SectionRenderer_instances = /* @__PURE__ */ new WeakSet();
renderSection_fn = async function(sectionId, { cache, mode, injectStylesheet, url }, abortController) {
  let sectionHTML = "";
  try {
    sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
  } catch (error) {
    if (abortController.signal.aborted) {
      const pendingRender = __privateGet(this, _pendingRendersBySectionId).get(sectionId);
      if (pendingRender && pendingRender.abortController !== abortController) {
        return pendingRender.promise;
      }
      return sectionHTML;
    }
    throw error;
  } finally {
    if (__privateGet(this, _abortControllersBySectionId).get(sectionId) === abortController) {
      __privateGet(this, _abortControllersBySectionId).delete(sectionId);
    }
    const pendingRender = __privateGet(this, _pendingRendersBySectionId).get(sectionId);
    if ((pendingRender == null ? void 0 : pendingRender.abortController) === abortController) {
      __privateGet(this, _pendingRendersBySectionId).delete(sectionId);
    }
  }
  if (!abortController.signal.aborted) {
    await morphSection(sectionId, sectionHTML, { mode, injectStylesheet });
  }
  return sectionHTML;
};
abortPendingMorph_fn = function(sectionId) {
  const existingAbortController = __privateGet(this, _abortControllersBySectionId).get(sectionId);
  if (existingAbortController) {
    existingAbortController.abort();
    __privateGet(this, _abortControllersBySectionId).delete(sectionId);
  }
};
cachePageSections_fn = function() {
  for (const section of document.querySelectorAll(".shopify-section")) {
    const url = buildSectionRenderingURL(section.id);
    if (__privateGet(this, _cache).get(url)) return;
    if (containsShadowRoot(section)) return;
    __privateGet(this, _cache).set(url, section.outerHTML);
  }
};
const SECTION_ID_PREFIX = "shopify-section-";
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set("section_id", normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}
function buildSectionSelector(sectionId) {
  return "".concat(SECTION_ID_PREFIX).concat(sectionId);
}
function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp("^".concat(SECTION_ID_PREFIX)), "");
}
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}
async function morphSection(sectionId, html, options = {}) {
  const { mode = "full", injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, "text/html");
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));
  if (!existingElement) {
    throw new Error("Section ".concat(sectionId, " not found"));
  }
  if (!newElement) {
    throw new Error("Section ".concat(sectionId, " not found in the section rendering response"));
  }
  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === "hydration"
  });
  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector("style[data-section-stylesheet]");
  if (!newStylesheet) return;
  const existingStylesheet = sectionElement.querySelector("style[data-section-stylesheet]");
  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}
const cartRequestTickets = /* @__PURE__ */ new WeakMap();
const pendingCartTickets = /* @__PURE__ */ new Set();
let cartRevision = 0;
function trackCartRequest(promise) {
  if (!promise || typeof promise.then !== "function") return null;
  if (cartRequestTickets.has(promise)) return cartRequestTickets.get(promise);
  const ticket = { started: ++cartRevision, finished: 0, overlapped: pendingCartTickets.size > 0 };
  for (const pending of pendingCartTickets) pending.overlapped = true;
  pendingCartTickets.add(ticket);
  cartRequestTickets.set(promise, ticket);
  const settled = () => {
    ticket.finished = ++cartRevision;
    pendingCartTickets.delete(ticket);
  };
  promise.then(settled, settled);
  return ticket;
}
async function renderCartSection(sectionId, html, options, ticket) {
  const signal = options && options.signal;
  const assertActive = () => {
    if (signal && signal.aborted || options && options.expectedElement && document.getElementById(buildSectionSelector(sectionId)) !== options.expectedElement) {
      const error = new Error("Cart render cancelled");
      error.name = "AbortError";
      throw error;
    }
  };
  await Promise.resolve();
  for (let attempt = 0; attempt < 3; attempt++) {
    assertActive();
    const revision = cartRevision;
    const bundledIsCurrent = ticket && !ticket.overlapped && ticket.finished === revision && ticket.finished === ticket.started + 1;
    if (!bundledIsCurrent || typeof html !== "string" || !html.trim()) {
      html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(window.location.href), signal);
    }
    assertActive();
    if (revision !== cartRevision) {
      html = null;
      continue;
    }
    if (options && options.requiredSelector) {
      const fragment = new DOMParser().parseFromString(html, "text/html");
      const section = fragment.getElementById(buildSectionSelector(sectionId));
      if (!section || !section.querySelector(options.requiredSelector)) throw new Error("Cart section content missing from response");
    }
    return morphSection(sectionId, html, options);
  }
  throw new Error("Cart changed during refresh; review the cart");
}
async function applyCurrentCartCount(count, ticket, controller, apply) {
  await Promise.resolve();
  for (let attempt = 0; attempt < 3; attempt++) {
    if (controller.signal.aborted) {
      const error = new Error("Cart count cancelled");
      error.name = "AbortError";
      throw error;
    }
    const revision = cartRevision;
    const current = ticket && !ticket.overlapped && ticket.finished === revision && ticket.finished === ticket.started + 1;
    if (!current || !Number.isInteger(count) || count < 0) {
      const cart = await fetchCartJson(Theme.routes.cart_url + ".js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store"
      }, controller, 15e3);
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error("Invalid cart count response");
      count = cart.item_count;
    }
    if (controller.signal.aborted) continue;
    if (revision !== cartRevision) {
      count = null;
      continue;
    }
    return apply(count);
  }
  throw new Error("Cart changed during count refresh");
}
async function applyCurrentCartItems(items, ticket, controller, apply) {
  const validItems = (value) => Array.isArray(value) && value.every((item) => item && item.variant_id != null && Number.isInteger(item.quantity) && item.quantity >= 0);
  await Promise.resolve();
  for (let attempt = 0; attempt < 3; attempt++) {
    if (controller.signal.aborted) {
      const error = new Error("Cart items refresh cancelled");
      error.name = "AbortError";
      throw error;
    }
    const revision = cartRevision;
    const current = ticket && !ticket.overlapped && ticket.finished === revision && ticket.finished === ticket.started + 1;
    if (!current || !validItems(items)) {
      const cart = await fetchCartJson(Theme.routes.cart_url + ".js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store"
      }, controller, 15e3);
      if (!cart || !validItems(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error("Invalid cart items response");
      items = cart.items;
    }
    if (controller.signal.aborted) continue;
    if (revision !== cartRevision) {
      items = null;
      continue;
    }
    return apply(items);
  }
  throw new Error("Cart changed during items refresh");
}
const sectionRenderer = new SectionRenderer();
export {
  applyCurrentCartCount,
  applyCurrentCartItems,
  buildSectionSelector,
  morphSection,
  normalizeSectionId,
  renderCartSection,
  sectionRenderer,
  trackCartRequest
};
